import { conditionalRender } from '@repo/toolkit/conditional-render'
import { toFullName } from '@repo/toolkit/to-full-name'
import DateStringInput from '@repo/ui/date-string-input'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import { getTransportMeans } from '../../../api/transport-means/get-transport-means'
import { getTravelCompanies } from '../../../api/travel-companies/get-travel-companies'
import { getTripProviders } from '../../../api/trip-providers/get-trip-providers'
import { getUsers } from '../../../api/user/get-users'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  TransportTypeEnum,
  transportTypeMapping,
  TripTypeEnum,
  tripTypeMapping
} from '../../../lib/types/enums/trip/trip-type.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { getCountryLabel } from '../../../lib/types/models/country/country-list'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

interface Props {
  filter?: TripBookingTypes.Filters
  onFilterChange: (filter: TripBookingTypes.Filters) => void
}

const TripBookingsFilter = ({ filter, onFilterChange }: Props) => {
  const { currentLanguage } = useTranslate()
  function setFilter<K extends keyof TripBookingTypes.Filters>(
    key: K,
    value: TripBookingTypes.Filters[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: users } = useQuery({
    queryKey: ['users', 'admins'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } })
  })

  const { data: transportMeans } = useQuery({
    queryKey: ['transport-means'],
    queryFn: () => getTransportMeans()
  })

  const { data: tripProviders } = useQuery({
    queryKey: ['trip-providers'],
    queryFn: () => getTripProviders()
  })

  const { data: travelCompanies } = useQuery({
    queryKey: ['travel-companies'],
    queryFn: () => getTravelCompanies()
  })

  const { data: countriesList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div />

      <div className="flex gap-2">
        <DateStringInput
          label="Date aller"
          value={filter?.departureDate}
          onChange={(date) => onFilterChange({ ...filter, departureDate: date })}
        />

        <DateStringInput
          label="Date retour"
          value={filter?.arrivalDate}
          onChange={(date) => onFilterChange({ ...filter, arrivalDate: date })}
        />
      </div>
      <SelectInput
        searchable
        label={'Pays de départ'}
        options={countriesList?.data.map((country) => ({
          label: getCountryLabel(currentLanguage, country) ?? '',
          value: getCountryLabel(currentLanguage, country)
        }))}
        onChange={(value) => {
          setFilter('departureCountry', value)
        }}
        selectedOption={conditionalRender(!!filter?.departureCountry, {
          value: filter?.departureCountry,
          label:
            getCountryLabel(
              currentLanguage,
              countriesList?.data.find(
                (country) => getCountryLabel(currentLanguage, country) === filter?.departureCountry
              )
            ) ?? ''
        })}
      />

      <Input
        type="text"
        label={'Ville de départ'}
        value={filter?.departureCity}
        onChange={(e) => setFilter('departureCity', e.target.value)}
      />

      <SelectInput
        searchable
        label={`Pays d'arrivée`}
        options={countriesList?.data.map((country) => ({
          label: getCountryLabel(currentLanguage, country) ?? '',
          value: getCountryLabel(currentLanguage, country)
        }))}
        onChange={(value) => {
          setFilter('arrivalCountry', value)
        }}
        selectedOption={conditionalRender(!!filter?.arrivalCountry, {
          value: filter?.arrivalCountry,
          label:
            getCountryLabel(
              currentLanguage,
              countriesList?.data.find(
                (country) => getCountryLabel(currentLanguage, country) === filter?.arrivalCountry
              )
            ) ?? ''
        })}
      />

      <Input
        type="text"
        label={"Ville d'arrivée"}
        value={filter?.arrivalCity}
        onChange={(e) => setFilter('arrivalCity', e.target.value)}
      />

      <div className="flex gap-2">
        <Input
          type={'number'}
          containerClassName={'flex-1'}
          label={'Total places réservées'}
          value={filter?.totalNumberOfPlaces}
          onChange={(e) => setFilter('totalNumberOfPlaces', Number(e.target.value))}
        />

        <Input
          type={'number'}
          containerClassName={'flex-1'}
          label={"Nombre d'encadrants"}
          value={filter?.numberOfStaffPlacesReserved}
          onChange={(e) => setFilter('numberOfStaffPlacesReserved', Number(e.target.value))}
        />
      </div>

      <MultiSelectInput
        label={'Resp résa'}
        variant={'primary'}
        searchable
        options={users?.data.map((user) => ({
          label: toFullName(user.firstName, user.lastName),
          value: user.id
        }))}
        selectedOptions={filter?.lecResponsableIds?.map((lecResponsableId) => {
          const user = users?.data.find((user) => user.id === lecResponsableId)
          return {
            label: toFullName(user?.firstName, user?.lastName),
            value: lecResponsableId
          }
        })}
        onChange={(lecResponsableIds) => setFilter('lecResponsableIds', lecResponsableIds)}
      />

      <MultiSelectInput
        label={'Type de voyage'}
        variant={'primary'}
        searchable
        options={Object.values(TripTypeEnum).map((tripType) => ({
          label: tripTypeMapping[tripType].label,
          value: tripType
        }))}
        selectedOptions={filter?.tripTypes?.map((tripType) => ({
          label: tripTypeMapping[tripType].label,
          value: tripType
        }))}
        onChange={(tripTypes) => setFilter('tripTypes', tripTypes)}
      />

      <MultiSelectInput
        label={'Type de trajet'}
        variant={'primary'}
        searchable
        options={Object.values(TransportTypeEnum).map((transportType) => ({
          label: transportTypeMapping[transportType].label,
          value: transportType
        }))}
        selectedOptions={filter?.directionalities?.map((transportType) => ({
          label: transportTypeMapping[transportType].label,
          value: transportType
        }))}
        onChange={(directionalities) => setFilter('directionalities', directionalities)}
      />

      <MultiSelectInput
        label={'Moyen de transport'}
        variant={'primary'}
        searchable
        options={transportMeans?.data.map((transportMean) => ({
          label: getTranslatedName(transportMean),
          value: transportMean.id
        }))}
        selectedOptions={filter?.transportMeansIds?.map((transportMeansId) => ({
          label: getTranslatedName(
            transportMeans?.data.find((transportMean) => transportMean.id === transportMeansId)
          ),
          value: transportMeansId
        }))}
        onChange={(transportMeansIds) => setFilter('transportMeansIds', transportMeansIds)}
      />

      <MultiSelectInput
        label={'Prestataire'}
        variant={'primary'}
        searchable
        options={tripProviders?.data.map((tripProvider) => ({
          label: tripProvider.companyName,
          value: tripProvider.id
        }))}
        selectedOptions={filter?.tripProviderIds?.map((tripProviderId) => ({
          label:
            tripProviders?.data.find((tripProvider) => tripProvider.id === tripProviderId)
              ?.companyName ?? '',
          value: tripProviderId
        }))}
        onChange={(tripProviderIds) => setFilter('tripProviderIds', tripProviderIds)}
      />

      <MultiSelectInput
        label="Compagnie"
        variant="primary"
        searchable
        options={travelCompanies?.data.map((travelCompany) => ({
          label: travelCompany.name,
          value: travelCompany.id
        }))}
        selectedOptions={filter?.travelCompanyIds?.map((travelCompanyId) => ({
          label:
            travelCompanies?.data.find((travelCompany) => travelCompany.id === travelCompanyId)
              ?.name ?? '',
          value: travelCompanyId
        }))}
        onChange={(travelCompanyIds) => setFilter('travelCompanyIds', travelCompanyIds)}
      />
    </div>
  )
}

export default TripBookingsFilter
